import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { blockKeys } from '@/features/calendars/useTimeBlocks';
import { taskKeys } from '@/features/tasks/useTasks';

export type FocusMode = 'idle' | 'working' | 'on_break' | 'paused';
export type FocusKind = 'work' | 'short_break' | 'long_break';

export interface FocusSettings {
  work_minutes: number;
  short_break_minutes: number;
  long_break_minutes: number;
  long_break_every: number;
  auto_start_breaks: boolean;
  auto_start_next_work: boolean;
  pause_task_on_break: boolean;
  daily_goal_pomodoros: number;
  block_duration_minutes: number;
  block_planning_minutes: number;
  block_sprints_per_block: number;
}

export interface FocusState {
  mode: FocusMode;
  pomodoro_enabled: boolean;
  active_task_id: number | null;
  active_task_title: string | null;
  resume_task_id: number | null;
  resume_task_title: string | null;
  previous_task_id: number | null;
  previous_task_title: string | null;
  active_work_block_id: number | null;
  active_calendar_event_id: number | null;
  active_calendar_event_title: string | null;
  active_client_id: number | null;
  active_client_name: string | null;
  active_project_id: number | null;
  active_project_name: string | null;
  /**
   * The client behind whatever is running, task timer or ad-hoc client timer
   * alike. `active_client_*` above means the narrower thing: that an ad-hoc
   * client timer is what is running.
   */
  effective_client_id: number | null;
  effective_client_name: string | null;
  effective_client_color: string | null;
  session_kind: FocusKind | null;
  session_started_at: string | null;
  session_ends_at: string | null;
  seconds_remaining: number;
  task_started_at: string | null;
  task_elapsed_seconds: number;
  cycle_index: number;
  pomodoros_completed_today: number;
  daily_goal_pomodoros: number;
  settings: FocusSettings;
}

const FOCUS_KEY = ['focus'] as const;

export function useFocus() {
  return useQuery<FocusState>({
    queryKey: FOCUS_KEY,
    queryFn: () => api<FocusState>('/api/focus'),
    // The server advances the machine on read, so polling doubles as the
    // mechanism that turns an expired interval into a break. Slow poll only;
    // the countdown itself is computed locally.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

export function useFocusControls() {
  const qc = useQueryClient();

  const run = (path: string, body?: unknown) =>
    api<FocusState>(`/api/focus/${path}`, { method: 'POST', json: body ?? {} });

  const settle = (data: FocusState) => {
    qc.setQueryData(FOCUS_KEY, data);
    qc.invalidateQueries({ queryKey: taskKeys.all });
    qc.invalidateQueries({ queryKey: ['timer'] });
    // Sprint start/pause/complete may create or resize planner blocks.
    qc.invalidateQueries({ queryKey: blockKeys.all });
  };

  const make = (path: string) =>
    useMutation<FocusState, ApiError, unknown | void>({
      mutationFn: (body) => run(path, body),
      onSuccess: settle,
      onError: (error) => toast.error(error.message),
    });

  return {
    start: make('start'),
    switchTask: make('switch'),
    pause: make('pause'),
    pauseTask: make('pause-task'),
    resume: make('resume'),
    resumePrevious: make('resume-previous'),
    clearPrevious: make('clear-previous'),
    skipBreak: make('skip-break'),
    extend: make('extend'),
    stop: make('stop'),
    startClient: useMutation<
      FocusState,
      ApiError,
      { client_id: number; project_id?: number | null }
    >({
      mutationFn: (body) => run('start-client', body),
      onSuccess: settle,
      onError: (error) => toast.error(error.message),
    }),
    startEvent: useMutation<FocusState, ApiError, { calendar_event_id: number }>({
      mutationFn: (body) => run('start-event', body),
      onSuccess: settle,
      onError: (error) => toast.error(error.message),
    }),
  };
}

/**
 * Seconds until a deadline, recomputed from the wall clock each tick.
 *
 * Never a decrementing counter: a backgrounded tab throttles intervals, so a
 * counter drifts while a difference of timestamps cannot.
 */
export function useCountdown(endsAt: string | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    const resync = () => setNow(Date.now());
    document.addEventListener('visibilitychange', resync);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', resync);
    };
  }, [endsAt]);

  if (!endsAt) return 0;
  return Math.max(0, Math.round((new Date(endsAt).getTime() - now) / 1000));
}

/** Refetch focus state when a local countdown reaches zero. */
export function useFocusSessionSync(
  endsAt: string | null,
  remaining: number,
  refetch: () => void,
) {
  const fired = useRef<string | null>(null);

  useEffect(() => {
    if (!endsAt || remaining > 0) return;
    if (fired.current === endsAt) return;
    fired.current = endsAt;
    refetch();
  }, [endsAt, remaining, refetch]);
}

/**
 * Fire once when an interval boundary is crossed.
 *
 * Notification, haptic, and the bar's own material change all happen together,
 * because feedback split across frames stops reading as one event.
 */
export function useBoundaryAlert(state: FocusState | undefined) {
  const lastKind = useRef<string | null>(null);

  useEffect(() => {
    if (!state) return;

    const key = `${state.mode}:${state.session_kind ?? ''}`;
    const previous = lastKind.current;
    lastKind.current = key;

    if (previous === null || previous === key) return;

    const startedBreak = state.mode === 'on_break';
    const backToWork = previous.startsWith('on_break') && state.mode === 'working';
    if (!startedBreak && !backToWork) return;

    const message = startedBreak
      ? `Break time: ${state.resume_task_title ?? 'your task'} is paused`
      : `Back to ${state.active_task_title ?? 'work'}`;

    toast.show(message);

    // Two short pulses. Reserved for real boundaries; haptics on everything
    // trains you to ignore them.
    navigator.vibrate?.([60, 40, 60]);

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Chronos', { body: message, tag: 'chronos-focus' });
    }
  }, [state]);
}

export function requestNotificationPermission(): void {
  if ('Notification' in window && Notification.permission === 'default') {
    void Notification.requestPermission();
  }
}

/** `5021` -> `1:23:41`. Always H:MM:SS for longer budgets. */
export function formatHms(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** `1500` -> `25:00`. Always mm:ss for a countdown. */
export function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
